<template>
  <div class="card">
    <img class="logo" src="/images/terrassa-logo.png" alt="Logo Terrassa" />
    <div class="vetlla-form">
      <h2>{{ titolFormulari }}</h2>
      <input
        v-model="nombreDifunto"
        class="form-input"
        type="text"
        placeholder="NOM DIFUNT"
      />
      <div class="datepicker-wrapper">
        <VueDatePicker
          v-model="fechaInicio"
          format="dd/MM/yyyy HH:mm"
          placeholder="Data inici"
          teleport-center
        />
      </div>
      <div class="datepicker-wrapper">
        <VueDatePicker
          v-model="fechaFin"
          format="dd/MM/yyyy HH:mm"
          placeholder="Data fi"
          teleport-center
        />
      </div>
      <div class="datepicker-wrapper">
        <VueDatePicker
          v-model="dataCerimonia"
          format="dd/MM/yyyy HH:mm"
          placeholder="Data cerimònia"
          teleport-center
        />
      </div>
      <input
        v-model="correoContacto"
        class="form-input"
        type="email"
        placeholder="CORREU CONTACTE"
      />
      <LlocCerimoniaSelector
        :llocCerimonia="llocCerimonia"
        :parroquia="parroquia"
        @update:llocCerimonia="llocCerimonia = $event"
        @update:parroquia="parroquia = $event"
      />
      <div class="tipos-qr">
        <div
          class="tipo-qr-radio-btn"
          :class="{ 'tipo-qr-radio-btn--selected': isObert }"
        >
          <input type="radio" id="obert" :value="true" v-model="isObert" />
          <label for="obert">QR Obert</label>
        </div>

        <div
          class="tipo-qr-radio-btn"
          :class="{ 'tipo-qr-radio-btn--selected': !isObert }"
        >
          <input type="radio" id="tancat" :value="false" v-model="isObert" />
          <label for="tancat">QR Tancat</label>
        </div>
      </div>
      <div class="qr-info-box" :class="{ 'qr-info-box--obert': isObert }">
        <div class="qr-info-row">
          <span class="qr-info-icon">ℹ️</span>
          <span v-if="isObert" class="qr-info-text">
            <strong>QR Obert:</strong> El contingut és visible immediatament. La
            família pot penjar fotos sense necessitat de validació.
          </span>
          <span v-else class="qr-info-text">
            <strong>QR Tancat:</strong> El contingut queda pendent de validació.
            Cal que la família accepti les fotos des de l'enllaç rebut per
            correu.
          </span>
        </div>
      </div>
      <div class="button-row">
        <div class="button-group-left">
          <button
            class="crea-button"
            @click="ejecutaAccion"
            :disabled="isButtonDesactivat"
          >
            <span v-if="isLoading" class="spinner"></span>
            <span>{{ isLoading ? "Creant…" : msgAccion }}</span>
          </button>
          <button class="tornar-button" @click="tornarEnrere">
            Tornar Enrere
          </button>
        </div>
        <button
          v-if="esModeEdita"
          class="elimina-button"
          @click="eliminaVetlla"
        >
          Elimina Vetlla
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import axios from "axios";
import { defineComponent, ref, onMounted } from "vue";
import { useRouter } from "vue-router";

import LlocCerimoniaSelector from "../components/LlocCerimoniaSelector.vue";
import CustomSwal from "../custom-swal.ts";
import { Vetlla } from "../types/vetlla.ts";

export default defineComponent({
  components: {
    LlocCerimoniaSelector,
  },
  props: {
    idVetlla: {
      required: false,
      type: Number,
    },
  },
  setup(props) {
    const router = useRouter();

    const nombreDifunto = ref("");
    const fechaInicio = ref("");
    const fechaFin = ref("");
    const correoContacto = ref("");
    const isObert = ref(false);
    const isButtonDesactivat = ref(false);
    const isLoading = ref(false);

    const llocCerimonia = ref("");
    const parroquia = ref("");
    const dataCerimonia = ref("");

    const validaVetlla = () => {
      if (
        nombreDifunto.value.trim() === "" ||
        fechaInicio.value === "" ||
        fechaFin.value === ""
      ) {
        CustomSwal.fire({
          icon: "warning",
          title: "Falten camps per emplenar!",
          text: "Tots els camps són obligatoris.",
        });
        return false;
      }

      if (!isObert.value) {
        if (correoContacto.value.trim() === "") {
          CustomSwal.fire({
            icon: "warning",
            title: "Falten camps per emplenar!",
            text: "El correu electrònic és obligatori si el QR està tancat.",
          });
          return false;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correoContacto.value)) {
          CustomSwal.fire({
            icon: "warning",
            title: "Adreça de correu invàlida",
          });
          return false;
        }
      }

      return true;
    };

    const ejecutaAccion = () => {
      if (isButtonDesactivat.value) return;

      if (validaVetlla()) {
        isButtonDesactivat.value = true;
        isLoading.value = true;

        const dateI = new Date(fechaInicio.value);
        const dateF = new Date(fechaFin.value);
        const dateC = new Date(dataCerimonia.value);

        const formatDate = (d: Date) =>
          `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
        axios
          .post<Vetlla>(apiRouteAccion, {
            ...(undefined != props.idVetlla && { id: props.idVetlla }),
            nombreDifunto: nombreDifunto.value,
            fechaInicio: formatDate(dateI),
            fechaFin: formatDate(dateF),
            dataCerimonia: formatDate(dateC),
            isQrObert: isObert.value,
            correoContacto: correoContacto.value,
            llocCerimonia: llocCerimonia.value,
            parroquia: parroquia.value,
          })
          .then((response) => {
            CustomSwal.fire({
              icon: "success",
              title: "Èxit",
            });
            router.push({
              name: "vetlles",
              params: {
                idVetlla: response.data.Id,
              },
            });
          })
          .finally(() => {
            isLoading.value = false;
            isButtonDesactivat.value = false;
          });
      }
    };

    let apiRouteAccion = "";

    const msgAccion = "Acceptar";
    const titolFormulari = ref("Nova Vetlla");
    const esModeEdita = ref(false);
    const isDeleting = ref(false);

    const eliminaVetlla = () => {
      CustomSwal.fire({
        icon: "warning",
        title: "Segur que vols eliminar aquesta vetlla?",
        text: "Aquesta acció no es pot desfer.",
        showCancelButton: true,
        confirmButtonText: "Elimina",
        cancelButtonText: "Cancel·la",
        confirmButtonColor: "#d33",
      }).then((result) => {
        if (result.isConfirmed && props.idVetlla) {
          isDeleting.value = true;
          axios
            .post("/api/vetlla/delete", { id: props.idVetlla })
            .then(() => {
              CustomSwal.fire({
                icon: "success",
                title: "Vetlla eliminada",
              });
              router.push({ name: "vetlles" });
            })
            .catch((error) => {
              const errorMsg =
                error.response?.data || "No s'ha pogut eliminar la vetlla.";
              CustomSwal.fire({
                icon: "error",
                title: "Error",
                text: errorMsg,
              });
            })
            .finally(() => {
              isDeleting.value = false;
            });
        }
      });
    };

    const tornarEnrere = () => {
      router.push({ name: "vetlles" });
    };

    onMounted(() => {
      if (undefined !== props.idVetlla) {
        esModeEdita.value = true;
        titolFormulari.value = "Edita Vetlla";
        apiRouteAccion = "/api/vetlla/edit";

        axios.get(`/api/vetlla/read?id=${props.idVetlla}`).then((response) => {
          const vetlla: Vetlla = response.data;
          nombreDifunto.value = vetlla.NombreDifunto;
          correoContacto.value = vetlla.CorreoContacto;
          isObert.value = vetlla.IsQrObert;

          const switchDateParts = (date: string) => {
            const day = date[0] + date[1];
            const month = date[3] + date[4];

            return month + "/" + day + date.substring(5);
          };
          fechaInicio.value = switchDateParts(vetlla.FechaInicio);
          fechaFin.value = switchDateParts(vetlla.FechaFin);
          llocCerimonia.value = vetlla.LlocCerimonia || "";
          parroquia.value = vetlla.Parroquia || "";
          dataCerimonia.value = switchDateParts(vetlla.DataCerimonia);
        });
      } else {
        apiRouteAccion = "/api/vetlla/create";
      }
    });

    return {
      nombreDifunto,
      fechaInicio,
      fechaFin,
      correoContacto,
      isObert,
      msgAccion,
      titolFormulari,
      ejecutaAccion,
      isButtonDesactivat,
      isLoading,
      llocCerimonia,
      parroquia,
      dataCerimonia,
      esModeEdita,
      eliminaVetlla,
      tornarEnrere,
    };
  },
});
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-evenly;

  padding: 1em;
  border-radius: $rounded-border;

  background-color: $bg-color;

  width: 80%;
}

.logo {
  width: 16em;
}

.vetlla-form {
  display: flex;
  flex-direction: column;
  gap: 1em;

  width: 24em;
}

.button-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 0.5em;
}

.button-group-left {
  display: flex;
  gap: 0.5em;
}

.crea-button {
  @include button($primary-color);
}

.elimina-button {
  @include button(#d32f2f);
}

.tornar-button {
  @include button($accessory-color);
  color: $text-color;
}

.tipos-qr {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1em;
}

.tipo-qr-radio-btn {
  > input {
    margin-right: 0.6em;
  }

  padding: 0.6em;
  background-color: $bg-color;
  border: 2px solid $accessory-color;
  border-radius: $rounded-border;
  flex-grow: 1;

  &--selected {
    background-color: $accessory-color;
  }

  &:hover {
    cursor: pointer;
    background-color: $accessory-color;
  }
}

.qr-info-box {
  padding: 0.8em;
  border-radius: $rounded-border;
  background-color: $primary-bg-color;
  border-left: 4px solid #e53e3e;
  transition: all 0.3s ease;

  &--obert {
    border-left-color: #48bb78;
  }
}

.qr-info-row {
  display: flex;
  align-items: flex-start;
  gap: 0.5em;
}

.qr-info-icon {
  font-size: 1.2em;
}

.qr-info-text {
  font-size: 0.9em;
  line-height: 1.4;
  text-align: left;
}

.spinner {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #333;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 0.8s linear infinite;
  display: inline-block;
  vertical-align: middle;
  margin-right: 0.5em;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
